import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, X, IndianRupee } from "lucide-react";

export default function Plans() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/platform/plans")).data });

  const FEATURES = [
    ["max_users", t("plans.maxUsers")],
    ["max_farmers", t("plans.maxFarmers")],
    ["max_daily_collections", t("plans.maxDaily")],
    ["storage_limit_mb", t("plans.storage")],
    ["support_priority", t("plans.support")],
  ];
  const BOOL_FEATURES = [
    ["export_features", t("plans.export")],
    ["api_access", t("plans.api")],
  ];

  return (
    <div className="space-y-6" data-testid="plans-page">
      <PageHeader title={t("plans.title")} subtitle={t("plans.subtitle")} breadcrumb={[t("crumb.platform"), t("plans.title")]} />
      {isLoading ? (
        <div className="grid gap-5 md:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-96" />)}</div>
      ) : (
        <div className="grid gap-5 md:grid-cols-3">
          {(data || []).map((p, idx) => (
            <Card key={p.key} className={`relative overflow-hidden ${idx === 1 ? "border-primary ring-1 ring-primary/30" : ""}`} data-testid={`plan-card-${p.key}`}>
              {idx === 1 && <div className="absolute right-0 top-0 rounded-bl-lg bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">{t("plans.popular")}</div>}
              <CardHeader>
                <CardTitle className="font-heading text-xl">{p.name}</CardTitle>
                <div className="flex items-baseline gap-1">
                  <IndianRupee className="h-5 w-5" />
                  <span className="font-heading text-3xl font-bold tnum">{p.price.toLocaleString("en-IN")}</span>
                  <span className="text-sm text-muted-foreground">{t("plans.month")}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-2.5 text-sm">
                {FEATURES.map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between border-b pb-2">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-medium tnum">{typeof p[k] === "number" ? p[k].toLocaleString("en-IN") : p[k]}</span>
                  </div>
                ))}
                {BOOL_FEATURES.map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-muted-foreground">{label}</span>
                    {p[k] ? <Check className="h-4 w-4 text-primary" /> : <X className="h-4 w-4 text-muted-foreground" />}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
